import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, Image as ImageIcon, Save, X, LogOut, FolderPlus, Layers, Settings, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
 
import { useNavigate } from 'react-router-dom';
import Login from '../components/Login';
import { BRAZO_INFO } from '../config';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default categories
const DEFAULT_CATEGORIES = [
  { id: 'brazos', name: 'Brazos Gitanos', icon: '🥧', description: 'Brazos gitanos artesanales' },
  { id: 'galletas', name: 'Galletas', icon: '🍪', description: 'Galletas frescas hechas en casa' },
  { id: 'galletas_especiales', name: 'Galletas Especiales', icon: '✨', description: 'Galletas especiales y únicas' },
  { id: 'desayunos', name: 'Desayunos', icon: '🥐', description: 'Desayunos completos y nutritivos' },
  { id: 'menu_sal', name: 'Menú Salado', icon: '🥗', description: 'Platos salados y empanadas' },
  { id: 'bebidas', name: 'Bebidas', icon: '☕', description: 'Bebidas frías y calientes' }
];

const Admin = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, authFetch } = useAuth();
  const [brazos, setBrazos] = useState([]);
  const [galletas, setGalletas] = useState([]);
  const [galletasEspeciales, setGalletasEspeciales] = useState([]);
  const [packs, setPacks] = useState([]);
  const [pasteles, setPasteles] = useState([]);
  const [panaderiaItems, setPanaderiaItems] = useState([]);
  const [cachitosItems, setCachitosItems] = useState([]);
  const [desayunosItems, setDesayunosItems] = useState([]);
  const [menuSalItems, setMenuSalItems] = useState([]);
  const [bebidasFriasItems, setBebidasFriasItems] = useState([]);
  const [bebidasCalientesItems, setBebidasCalientesItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customProducts, setCustomProducts] = useState({});
  const [editDialog, setEditDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [itemType, setItemType] = useState('brazo');
  const [activeTab, setActiveTab] = useState('brazos');
  
  // WhatsApp config state
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  useEffect(() => {
    // Load WhatsApp config from backend (public read)
    loadConfig();
  }, []);
  
  // Load products when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔄 Loading products because authenticated');
      loadProducts();
    }
  }, [isAuthenticated]);
  
  const loadProducts = async () => {
    try {
      // Load brazos
      const brazosResponse = await fetch(`${BACKEND_URL}/api/products?category=brazos`);
      if (brazosResponse.ok) {
        const brazosData = await brazosResponse.json();
        console.log('✅ Loaded brazos from API:', brazosData.length);
        const mappedBrazos = brazosData.map(b => ({
          id: b.id,  // Keep as string from MongoDB
          name: b.name,
          description: b.description,
          priceSlice: b.priceSlice,
          priceFull: b.priceFull,
          image: b.image,
          category: b.brazo_category || 'Clásico',
          availableSlice: b.availableSlice !== false,
          availableFull: b.availableFull !== false,
          bajoPedido: b.bajoPedido === true,
          sortOrder: b.sortOrder || 999,
          available: b.available !== false
        }));
        setBrazos(mappedBrazos);
        console.log('✅ Set brazos state:', mappedBrazos.length);
      }
      
      // Load galletas
      const galletasResponse = await fetch(`${BACKEND_URL}/api/products?category=galletas`);
      if (galletasResponse.ok) {
        const galletasData = await galletasResponse.json();
        console.log('✅ Loaded galletas from API:', galletasData.length);
        const mappedGalletas = galletasData.map(g => ({
          id: g.id,  // Keep as string from MongoDB
          name: g.name,
          description: g.description,
          priceUnit: g.priceUnit || 1.50,
          pricePack5: g.pricePack5 || 6.00,
          pricePack12: g.pricePack12 || 14.00,
          image: g.image,
          available: g.available !== false
        }));
        setGalletas(mappedGalletas);
        console.log('✅ Set galletas state:', mappedGalletas.length);
      }
      
      // Load packs
      const packsResponse = await fetch(`${BACKEND_URL}/api/products?category=packs`);
      if (packsResponse.ok) {
        const packsData = await packsResponse.json();
        console.log('✅ Loaded packs from API:', packsData.length);
        const mappedPacks = packsData.map(p => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          description: p.description,
          price: p.price || 0,
          quantity: p.quantity,
          basePrice: p.basePrice,
          minQuantity: p.minQuantity,
          maxQuantity: p.maxQuantity,
          packType: p.packType,
          image: p.image,
          available: p.available !== false
        }));
        setPacks(mappedPacks);
        console.log('✅ Set packs state:', mappedPacks.length);
      }

      // Load Galletas Especiales
      const galletasEspecialesResponse = await fetch(`${BACKEND_URL}/api/products?category=galletas_especiales`);
      if (galletasEspecialesResponse.ok) {
        const galletasEspecialesData = await galletasEspecialesResponse.json();
        const mappedGalletasEspeciales = galletasEspecialesData.map(g => ({
          id: g.id,
          name: g.name,
          description: g.description,
          price: g.price || 0,
          image: g.image,
          available: g.available !== false
        }));
        setGalletasEspeciales(mappedGalletasEspeciales);
      }

      
      // Load pasteles
      try {
        const pastelesResponse = await fetch(`${BACKEND_URL}/api/products?category=pasteles`);
        if (pastelesResponse.ok) {
          const pastelesData = await pastelesResponse.json();
          console.log('✅ Loaded pasteles from API:', pastelesData.length, pastelesData);
          const mappedPasteles = pastelesData.map(p => ({
            id: p.id,  // Keep as string from MongoDB
            name: p.name,
            description: p.description,
            price: p.price || 0,
            portions: p.portions,
            image: p.image,
            isConfigurable: p.isConfigurable || false,
            isTresLeches: p.isTresLeches || false,
            flavors: p.flavors || [],
            sizes: p.sizes || [],
            comboPrice: p.comboPrice || null,
            comboDescription: p.comboDescription || '',
            available: p.available !== false
          }));
          console.log('✅ Mapped pasteles:', mappedPasteles.length, mappedPasteles);
          setPasteles(mappedPasteles);
          console.log('✅ Set pasteles state');
        }
      } catch (pastelesError) {
        console.error('❌ Error loading pasteles:', pastelesError);
      }
      
      // Load panaderia AND cachitos (consolidated)
      const panaderiaResponse = await fetch(`${BACKEND_URL}/api/products?category=panaderia`);
      const cachitosResponse = await fetch(`${BACKEND_URL}/api/products?category=cachitos`);
      
      let allPanaderia = [];
      
      if (panaderiaResponse.ok) {
        const panaderiaData = await panaderiaResponse.json();
        const mappedPanaderia = panaderiaData.map(p => ({
          id: p.id,  // Keep as string from MongoDB
          name: p.name,
          description: p.description,
          price: p.price || 0,
          image: p.image,
          hasOption: p.hasOption || false,
          options: p.options || [],
          available: p.available !== false,
          dbCategory: 'panaderia'
        }));
        allPanaderia = [...mappedPanaderia];
      }
      
      if (cachitosResponse.ok) {
        const cachitosData = await cachitosResponse.json();
        const mappedCachitos = cachitosData.map(c => ({
          id: c.id,  // Keep as string from MongoDB
          name: c.name,
          price: c.price || 0,
          image: c.image,
          available: c.available !== false,
          dbCategory: 'cachitos'
        }));
        allPanaderia = [...allPanaderia, ...mappedCachitos];
      }
      
      setPanaderiaItems(allPanaderia);
      setCachitosItems([]);
      
      // Load new categories: desayunos, menu_sal, bebidas (frias y calientes)
      const desayunosResponse = await fetch(`${BACKEND_URL}/api/products?category=desayunos`);
      if (desayunosResponse.ok) {
        const desayunosData = await desayunosResponse.json();
        const mappedDesayunos = desayunosData.map(d => ({
          id: d.id,
          name: d.name,
          description: d.description,
          price: d.price || 0,
          image: d.image,
          hasOption: d.hasOption || false,
          options: d.options || [],
          available: d.available !== false
        }));
        setDesayunosItems(mappedDesayunos);
      }
      
      const menuSalResponse = await fetch(`${BACKEND_URL}/api/products?category=menu_sal`);
      if (menuSalResponse.ok) {
        const menuSalData = await menuSalResponse.json();
        const mappedMenuSal = menuSalData.map(m => ({
          id: m.id,
          name: m.name,
          description: m.description,
          price: m.price || 0,
          image: m.image,
          hasOption: m.hasOption || false,
          options: m.options || [],
          available: m.available !== false
        }));
        setMenuSalItems(mappedMenuSal);
      }
      
      const bebidasFriasResponse = await fetch(`${BACKEND_URL}/api/products?category=bebidas&subcategory=frias`);
      if (bebidasFriasResponse.ok) {
        const bebidasFriasData = await bebidasFriasResponse.json();
        const mappedBebidasFrias = bebidasFriasData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          subcategory: 'frias',
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        }));
        setBebidasFriasItems(mappedBebidasFrias);
      }
      
      const bebidasCalientesResponse = await fetch(`${BACKEND_URL}/api/products?category=bebidas&subcategory=calientes`);
      if (bebidasCalientesResponse.ok) {
        const bebidasCalientesData = await bebidasCalientesResponse.json();
        const mappedBebidasCalientes = bebidasCalientesData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          subcategory: 'calientes',
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        }));
        setBebidasCalientesItems(mappedBebidasCalientes);
      }
      
      // Categories (still from localStorage for now)
      const savedCategories = localStorage.getItem('customCategories');
      const savedCustomProducts = localStorage.getItem('customProducts');
      setCategories(savedCategories ? JSON.parse(savedCategories) : []);
      setCustomProducts(savedCustomProducts ? JSON.parse(savedCustomProducts) : {});
      
    } catch (error) {
      console.error('Error loading products from backend:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/config`);
      if (response.ok) {
        const data = await response.json();
        setWhatsappNumber(data.whatsapp_number || '593984814807');
        setBusinessName(data.business_name || 'Brazolovers');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      // Use defaults from localStorage or hardcoded
      setWhatsappNumber('593984814807');
      setBusinessName('Brazolovers');
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    setConfigSaved(false);
    try {
      const response = await authFetch(`${BACKEND_URL}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          whatsapp_number: whatsappNumber,
          business_name: businessName
        })
      });
      if (response.ok) {
        setConfigSaved(true);
        setTimeout(() => setConfigSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error al guardar la configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  const saveProductToBackend = async (product, category) => {
    try {
      const payload = {
        category,
        subcategory: product.subcategory || null,
        name: product.name,
        slug: product.slug || null,
        description: product.description || '',
        image: product.image || '',
        available: product.available !== false,
        priceSlice: product.priceSlice ? parseFloat(product.priceSlice) : null,
        priceFull: product.priceFull ? parseFloat(product.priceFull) : null,
        brazo_category: product.category || null,
        availableSlice: product.availableSlice !== false,
        availableFull: product.availableFull !== false,
        bajoPedido: product.bajoPedido === true,
        sortOrder: product.sortOrder ? parseInt(product.sortOrder) : 999,
        priceUnit: product.priceUnit ? parseFloat(product.priceUnit) : null,
        pricePack5: product.pricePack5 ? parseFloat(product.pricePack5) : null,
        pricePack12: product.pricePack12 ? parseFloat(product.pricePack12) : null,
        quantity: product.quantity ? parseInt(product.quantity) : null,
        basePrice: product.basePrice ? parseFloat(product.basePrice) : null,
        minQuantity: product.minQuantity ? parseInt(product.minQuantity) : null,
        maxQuantity: product.maxQuantity ? parseInt(product.maxQuantity) : null,
        packType: product.packType || null,
        price: product.price ? parseFloat(product.price) : null,
        portions: product.portions || null,
        isConfigurable: product.isConfigurable || false,
        isTresLeches: product.isTresLeches || false,
        flavors: product.flavors || null,
        sizes: product.sizes || null,
        comboPrice: product.comboPrice ? parseFloat(product.comboPrice) : null,
        comboDescription: product.comboDescription || null,
        hasOption: product.hasOption || false,
        options: product.options || null
      };

      // Use string ID from MongoDB, not numeric ID
      const productId = String(product.id);
      const isNew = !product.id || product.id > 1000000000;
      
      if (isNew) {
        const response = await authFetch(`${BACKEND_URL}/api/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Backend error:', response.status, errorText);
          throw new Error(`Error al crear producto: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('✅ Producto creado en BD:', result);
      } else {
        const response = await authFetch(`${BACKEND_URL}/api/products/${productId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Backend error:', response.status, errorText);
          throw new Error(`Error al actualizar producto: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('✅ Producto actualizado en BD:', result);
      }
      
      // Clear cache after save
      try {
        await authFetch(`${BACKEND_URL}/api/cache/clear`, { method: 'POST' });
        console.log('✅ Cache cleared');
      } catch (cacheError) {
        console.warn('⚠️ Cache clear failed:', cacheError);
      }
      
      await loadProducts();
      alert('✅ Producto guardado exitosamente');
    } catch (error) {
      console.error('❌ Error saving product:', error);
      alert('❌ Error al guardar el producto: ' + error.message);
    }
  };

  const deleteProductFromBackend = async (productId) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/products/${productId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Error al eliminar producto');
      }
      
      console.log('✅ Producto eliminado de BD:', productId);
      await loadProducts();
      alert('✅ Producto eliminado exitosamente');
    } catch (error) {
      console.error('❌ Error deleting product:', error);
      alert('❌ Error al eliminar el producto: ' + error.message);
    }
  };

  const saveCategories = (newCategories) => {
    setCategories(newCategories);
    localStorage.setItem('customCategories', JSON.stringify(newCategories));
  };

  const saveCustomProducts = (newProducts) => {
    setCustomProducts(newProducts);
    localStorage.setItem('customProducts', JSON.stringify(newProducts));
  };

  // Category management
  const handleAddCategory = () => {
    setCurrentCategory({
      id: `cat_${Date.now()}`,
      name: '',
      icon: '📦',
      description: '',
      priceLabel: 'Precio',
      hasVariants: false
    });
    setCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory({ ...category });
    setCategoryDialog(true);
  };

  const handleSaveCategory = () => {
    if (!currentCategory.name.trim()) {
      alert('El nombre de la categoría es requerido');
      return;
    }
    
    const existingIndex = categories.findIndex(c => c.id === currentCategory.id);
    let newCategories;
    
    if (existingIndex >= 0) {
      newCategories = categories.map(c => c.id === currentCategory.id ? currentCategory : c);
    } else {
      newCategories = [...categories, currentCategory];
      // Initialize empty products array for new category
      saveCustomProducts({ ...customProducts, [currentCategory.id]: [] });
    }
    
    saveCategories(newCategories);
    setCategoryDialog(false);
    setCurrentCategory(null);
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría y todos sus productos?')) {
      saveCategories(categories.filter(c => c.id !== categoryId));
      const newCustomProducts = { ...customProducts };
      delete newCustomProducts[categoryId];
      saveCustomProducts(newCustomProducts);
    }
  };

  // Custom category products management
  const handleAddCustomProduct = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setCurrentItem({
      id: Date.now(),
      name: '',
      description: '',
      price: 0,
      image: '',
      available: true,
      categoryId: categoryId
    });
    setItemType(categoryId);
    setEditDialog(true);
  };

  const handleSaveCustomProduct = () => {
    const categoryId = currentItem.categoryId;
    const categoryProducts = customProducts[categoryId] || [];
    const existingIndex = categoryProducts.findIndex(p => p.id === currentItem.id);
    
    let newProducts;
    if (existingIndex >= 0) {
      newProducts = categoryProducts.map(p => p.id === currentItem.id ? currentItem : p);
    } else {
      newProducts = [...categoryProducts, currentItem];
    }
    
    saveCustomProducts({ ...customProducts, [categoryId]: newProducts });
    setEditDialog(false);
    setCurrentItem(null);
  };

  const handleDeleteCustomProduct = (categoryId, productId) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      const categoryProducts = customProducts[categoryId] || [];
      saveCustomProducts({ 
        ...customProducts, 
        [categoryId]: categoryProducts.filter(p => p.id !== productId) 
      });
    }
  };

  const handleEdit = (item, type) => {
    setCurrentItem({ ...item });
    setItemType(type);
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (currentItem?.categoryId) {
      handleSaveCustomProduct();
      return;
    }
    
    let category = '';
    if (itemType === 'brazo') category = 'brazos';
    else if (itemType === 'galleta') category = 'galletas';
    else if (itemType === 'galleta_especial') category = 'galletas_especiales';
    else if (itemType === 'pack') category = 'packs';
    else if (itemType === 'pastel') category = 'pasteles';
    else if (itemType === 'panaderia') {
      category = currentItem.dbCategory || 'panaderia';
    }
    else if (itemType === 'desayuno') category = 'desayunos';
    else if (itemType === 'menu_sal') category = 'menu_sal';
    else if (itemType === 'bebida') category = 'bebidas';
    
    await saveProductToBackend(currentItem, category);
    setEditDialog(false);
    setCurrentItem(null);
  };

  const handleAdd = (type) => {
    let newItem;
    if (type === 'brazo') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        priceSlice: 0,
        priceFull: 0,
        image: '',
        category: 'Clásico',
        availableSlice: true,
        availableFull: true,
        bajoPedido: false,
        sortOrder: 999
      };
    } else if (type === 'galleta') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        priceUnit: 1.50,
        pricePack5: 6.00,
        pricePack12: 14.00,
        image: '',
        available: true
      };
    } else if (type === 'galleta_especial') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        image: '',
        available: true,
        dbCategory: 'galletas_especiales'
      };

    } else if (type === 'pastel') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        portions: '',
        image: '',
        isConfigurable: false,
        sizes: [],
        available: true
      };
    } else if (type === 'panaderia') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        image: '',
        hasOption: false,
        options: [],
        available: true,
        dbCategory: 'panaderia'
      };
    } else if (type === 'desayuno') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        image: '',
        hasOption: false,
        options: [],
        available: true,
        dbCategory: 'desayunos'
      };
    } else if (type === 'menu_sal') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        image: '',
        hasOption: false,
        options: [],
        available: true,
        dbCategory: 'menu_sal'
      };
    } else if (type === 'bebida') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        image: '',
        subcategory: 'frias',  // default
        hasOption: false,
        options: [],
        available: true,
        dbCategory: 'bebidas'
      };
    } else if (type === 'pack') {
      newItem = {
        id: Date.now(),
        name: '',
        description: '',
        price: 0,
        quantity: 5,
        basePrice: 14.00,  // Default base price for custom packs
        minQuantity: 13,
        maxQuantity: 100,
        packType: 'fixed',  // Changed default to 'fixed'
        image: '',
        available: true
      };
    }
    setCurrentItem(newItem);
    setItemType(type);
    setEditDialog(true);
  };

  const handleDelete = async (id, type) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      await deleteProductFromBackend(id);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-chocolate text-white py-6 px-6 shadow-lg">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:text-caramel hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver al Sitio
            </Button>
            <h1 className="text-3xl font-bold">Panel de Administración</h1>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-white hover:text-red-400 hover:bg-white/10"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <TabsList className="flex flex-wrap">
              <TabsTrigger value="brazos">🥧 Brazos Gitanos ({brazos.length})</TabsTrigger>
              <TabsTrigger value="galletas">🍪 Galletas ({galletas.length})</TabsTrigger>
              <TabsTrigger value="galletas_especiales">✨ Galletas Especiales ({galletasEspeciales.length})</TabsTrigger>
              <TabsTrigger value="packs">📦 Packs de Galletas ({packs.length})</TabsTrigger>
              <TabsTrigger value="pasteles">🎂 Pasteles ({pasteles.length})</TabsTrigger>
              <TabsTrigger value="panaderia">🥖 Panadería ({panaderiaItems.length})</TabsTrigger>
              <TabsTrigger value="desayunos">🥐 Desayunos ({desayunosItems.length})</TabsTrigger>
              <TabsTrigger value="menu_sal">🥗 Menú Salado ({menuSalItems.length})</TabsTrigger>
              <TabsTrigger value="bebidas">☕ Bebidas ({bebidasFriasItems.length + bebidasCalientesItems.length})</TabsTrigger>
              {categories.map(cat => (
                <TabsTrigger key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name} ({(customProducts[cat.id] || []).length})
                </TabsTrigger>
              ))}
              <TabsTrigger value="config" className="bg-blue-100 text-blue-800">
                <Settings className="w-4 h-4 mr-1" />
                Configuración
              </TabsTrigger>
              <TabsTrigger value="categorias" className="bg-green-100 text-green-800">
                <FolderPlus className="w-4 h-4 mr-1" />
                Categorías
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Brazos Tab */}
          <TabsContent value="brazos" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">Gestionar Brazos Gitanos</h2>
              <Button onClick={() => handleAdd('brazo')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {brazos.map((brazo) => (
                <Card key={brazo.id} className={`${!brazo.available ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{brazo.name || 'Sin nombre'}</CardTitle>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded ${brazo.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {brazo.available ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {brazo.image && (
                      <img src={getImageUrl(brazo.image)} alt={brazo.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    <p className="text-sm text-chocolate-light line-clamp-2">{brazo.description}</p>
                    <div className="flex justify-between text-sm">
                      <span>Porción: ${brazo.priceSlice}</span>
                      <span>Completo: ${brazo.priceFull}</span>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(brazo, 'brazo')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(brazo.id, 'brazo')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Galletas Tab */}
          <TabsContent value="galletas" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">Gestionar Galletas</h2>
              <Button onClick={() => handleAdd('galleta')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nueva
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galletas.map((galleta) => (
                <Card key={galleta.id} className={`${!galleta.available ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{galleta.name || 'Sin nombre'}</CardTitle>
                      <span className={`px-2 py-1 text-xs rounded ${galleta.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {galleta.available ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {galleta.image && (
                      <img src={getImageUrl(galleta.image)} alt={galleta.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    <p className="text-sm text-chocolate-light line-clamp-2">{galleta.description}</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Unidad:</span>
                        <span className="font-semibold">${galleta.priceUnit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pack x5:</span>
                        <span className="font-semibold">${galleta.pricePack5}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pack x12:</span>
                        <span className="font-semibold">${galleta.pricePack12}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(galleta, 'galleta')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(galleta.id, 'galleta')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>


          {/* Galletas Especiales Tab */}
          <TabsContent value="galletas_especiales" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">✨ Gestionar Galletas Especiales</h2>
              <Button onClick={() => handleAdd('galleta_especial')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nueva
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galletasEspeciales.map((item) => (
                <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                      <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.available !== false ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {item.image && (
                      <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    {item.description && (
                      <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                    )}
                    <div className="text-lg font-bold text-caramel">${item.price?.toFixed(2)}</div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(item, 'galleta_especial')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(item.id, 'galleta_especial')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>


          {/* Packs Tab */}
          <TabsContent value="packs" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">📦 Gestionar Packs de Galletas</h2>
              <Button onClick={() => handleAdd('pack')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo Pack
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packs.map((pack) => (
                <Card key={pack.id} className={`${!pack.available ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{pack.name || 'Sin nombre'}</CardTitle>
                      <span className={`px-2 py-1 text-xs rounded ${pack.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {pack.available ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pack.image && (
                      <img src={pack.image} alt={pack.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    <p className="text-sm text-chocolate-light line-clamp-2">{pack.description}</p>
                    <div className="text-xs space-y-1">
                      {pack.packType === 'pack' ? (
                        <>
                          <div className="flex justify-between">
                            <span>Cantidad:</span>
                            <span className="font-semibold">{pack.quantity} galletas</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Precio:</span>
                            <span className="font-semibold">${pack.price?.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span>Precio base:</span>
                            <span className="font-semibold">${pack.basePrice?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Cantidad mín/máx:</span>
                            <span className="font-semibold">{pack.minQuantity} - {pack.maxQuantity}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(pack, 'pack')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(pack.id, 'pack')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Pasteles Tab */}
          <TabsContent value="pasteles" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">🎂 Gestionar Pasteles & Roscas</h2>
              <Button onClick={() => handleAdd('pastel')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pasteles.map((pastel) => (
                <Card key={pastel.id} className={`${pastel.available === false ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{pastel.name || 'Sin nombre'}</CardTitle>
                      <div className="flex items-center gap-2">
                        {pastel.isConfigurable && (
                          <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-800">
                            Configurable
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded ${pastel.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {pastel.available !== false ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pastel.image && (
                      <img src={getImageUrl(pastel.image)} alt={pastel.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    <p className="text-sm text-chocolate-light line-clamp-2">{pastel.description}</p>
                    <div className="flex justify-between text-sm">
                      {pastel.isConfigurable ? (
                        <span className="text-purple-600">Precio según configuración</span>
                      ) : (
                        <span className="text-lg font-bold text-caramel">${pastel.price}</span>
                      )}
                      {pastel.portions && <span className="text-chocolate-light">{pastel.portions}</span>}
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(pastel, 'pastel')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(pastel.id, 'pastel')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Panadería Tab */}
          <TabsContent value="panaderia" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">🥖 Gestionar Panadería y Cachitos</h2>
              <Button onClick={() => handleAdd('panaderia')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {panaderiaItems.map((item) => (
                <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                        {item.dbCategory === 'cachitos' && (
                          <span className="text-xs text-caramel">🥐 Cachito</span>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.available !== false ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {item.image && (
                      <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    {item.description && (
                      <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                    )}
                    <div className="text-lg font-bold text-caramel">${item.price}</div>
                    {item.hasOption && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Con opciones</span>
                    )}
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(item, 'panaderia')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(item.id, 'panaderia')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Desayunos Tab */}
          <TabsContent value="desayunos" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">🥐 Gestionar Desayunos</h2>
              <Button onClick={() => handleAdd('desayuno')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {desayunosItems.map((item) => (
                <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                      <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.available !== false ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {item.image && (
                      <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    {item.description && (
                      <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                    )}
                    <div className="text-lg font-bold text-caramel">${item.price}</div>
                    {item.hasOption && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Con opciones</span>
                    )}
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(item, 'desayuno')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(item.id, 'desayuno')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Menú Salado Tab */}
          <TabsContent value="menu_sal" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-chocolate">🥗 Gestionar Menú Salado</h2>
              <Button onClick={() => handleAdd('menu_sal')} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {menuSalItems.map((item) => (
                <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                      <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.available !== false ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {item.image && (
                      <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                    )}
                    {item.description && (
                      <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                    )}
                    <div className="text-lg font-bold text-caramel">${item.price}</div>
                    {item.hasOption && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Con opciones</span>
                    )}
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEdit(item, 'menu_sal')}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDelete(item.id, 'menu_sal')}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bebidas Tab */}
          <TabsContent value="bebidas" className="space-y-6">
            <h2 className="text-2xl font-bold text-chocolate text-center">☕ Gestionar Bebidas</h2>
            
            {/* Bebidas Frías */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-blue-600">🧊 Bebidas Frías</h3>
                <Button onClick={() => { handleAdd('bebida'); setCurrentItem(prev => ({ ...prev, subcategory: 'frias' })); }} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Bebida Fría
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bebidasFriasItems.map((item) => (
                  <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                        <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.available !== false ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {item.image && (
                        <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                      )}
                      {item.description && (
                        <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                      )}
                      <div className="text-lg font-bold text-caramel">${item.price}</div>
                      {item.hasOption && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Con opciones</span>
                      )}
                      <div className="flex space-x-2">
                        <Button 
                          onClick={() => handleEdit(item, 'bebida')}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          onClick={() => handleDelete(item.id, 'bebida')}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Bebidas Calientes */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-orange-600">☕ Bebidas Calientes</h3>
                <Button onClick={() => { handleAdd('bebida'); setCurrentItem(prev => ({ ...prev, subcategory: 'calientes' })); }} className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Bebida Caliente
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bebidasCalientesItems.map((item) => (
                  <Card key={item.id} className={`${item.available === false ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{item.name || 'Sin nombre'}</CardTitle>
                        <span className={`px-2 py-1 text-xs rounded ${item.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.available !== false ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {item.image && (
                        <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-32 object-cover rounded-lg" />
                      )}
                      {item.description && (
                        <p className="text-sm text-chocolate-light line-clamp-2">{item.description}</p>
                      )}
                      <div className="text-lg font-bold text-caramel">${item.price}</div>
                      {item.hasOption && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Con opciones</span>
                      )}
                      <div className="flex space-x-2">
                        <Button 
                          onClick={() => handleEdit(item, 'bebida')}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          onClick={() => handleDelete(item.id, 'bebida')}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>


          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-4">
            <div className="max-w-2xl">
              <h2 className="text-2xl font-bold text-chocolate mb-6">⚙️ Configuración del Negocio</h2>
              
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    Configuración de WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">Número de WhatsApp (con código de país)</Label>
                    <Input
                      id="whatsapp"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      placeholder="593984814807"
                      className="font-mono"
                    />
                    <p className="text-xs text-chocolate-light">
                      Ejemplo: 593984814807 (Ecuador +593)
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Nombre del Negocio</Label>
                    <Input
                      id="businessName"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Brazolovers"
                    />
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button 
                      onClick={saveConfig}
                      disabled={savingConfig}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {savingConfig ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Guardar Configuración
                        </>
                      )}
                    </Button>
                    {configSaved && (
                      <span className="ml-4 text-green-600 font-semibold">
                        ✓ Guardado correctamente
                      </span>
                    )}
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <p className="text-sm font-semibold text-amber-900 mb-2">💡 Información</p>
                    <p className="text-xs text-amber-800">
                      El número de WhatsApp configurado aquí será usado para recibir todos los pedidos de la página web.
                      Asegúrate de incluir el código de país sin el signo +.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Custom Categories Tabs */}
          {categories.map(category => (
            <TabsContent key={category.id} value={category.id} className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-chocolate flex items-center gap-2">
                    <span className="text-3xl">{category.icon}</span>
                    Gestionar {category.name}
                  </h2>
                  {category.description && (
                    <p className="text-chocolate-light mt-1">{category.description}</p>
                  )}
                </div>
                <Button onClick={() => handleAddCustomProduct(category.id)} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Nuevo
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(customProducts[category.id] || []).map((product) => (
                  <Card key={product.id} className={`${!product.available ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{product.name || 'Sin nombre'}</CardTitle>
                        <span className={`px-2 py-1 text-xs rounded ${product.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {product.available ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {product.image && (
                        <img src={getImageUrl(product.image)} alt={product.name} className="w-full h-32 object-cover rounded-lg" />
                      )}
                      <p className="text-sm text-chocolate-light line-clamp-2">{product.description}</p>
                      <div className="text-lg font-bold text-caramel">
                        ${product.price?.toFixed(2) || '0.00'}
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          onClick={() => {
                            setCurrentItem({ ...product, categoryId: category.id });
                            setItemType(category.id);
                            setEditDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          onClick={() => handleDeleteCustomProduct(category.id, product.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {(customProducts[category.id] || []).length === 0 && (
                  <div className="col-span-full text-center py-12 bg-cream rounded-lg">
                    <Layers className="w-16 h-16 mx-auto mb-4 text-chocolate-light opacity-50" />
                    <p className="text-chocolate-light">No hay productos en esta categoría</p>
                    <Button 
                      onClick={() => handleAddCustomProduct(category.id)} 
                      className="mt-4 bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar el primero
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}

          {/* Categories Management Tab */}
          <TabsContent value="categorias" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-chocolate">Gestionar Categorías</h2>
                <p className="text-chocolate-light">Crea nuevas categorías de productos (ej: Cakes, Pasteles, etc.)</p>
              </div>
              <Button onClick={handleAddCategory} className="bg-green-600 hover:bg-green-700">
                <FolderPlus className="w-4 h-4 mr-2" />
                Nueva Categoría
              </Button>
            </div>

            {/* Default Categories Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Categorías Base</h3>
              <p className="text-sm text-blue-800">
                Las categorías Brazos Gitanos y Galletas son categorías base y no pueden ser eliminadas.
              </p>
            </div>

            {/* Custom Categories */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <Card key={category.id} className="border-2 border-green-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="text-2xl">{category.icon}</span>
                        {category.name}
                      </CardTitle>
                      <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">
                        {(customProducts[category.id] || []).length} productos
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-chocolate-light">{category.description || 'Sin descripción'}</p>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleEditCategory(category)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        onClick={() => handleDeleteCategory(category.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {categories.length === 0 && (
                <div className="col-span-full text-center py-12 bg-cream rounded-lg">
                  <FolderPlus className="w-16 h-16 mx-auto mb-4 text-chocolate-light opacity-50" />
                  <p className="text-chocolate-light mb-2">No hay categorías personalizadas</p>
                  <p className="text-sm text-chocolate-light mb-4">
                    Crea categorías como Cakes, Pasteles, Tortas, etc.
                  </p>
                  <Button onClick={handleAddCategory} className="bg-green-600 hover:bg-green-700">
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Crear primera categoría
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentItem?.categoryId 
                ? `${currentItem.id > 1000000000 ? 'Agregar' : 'Editar'} ${categories.find(c => c.id === currentItem.categoryId)?.name || 'Producto'}`
                : itemType === 'brazo' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Brazo Gitano`
                : itemType === 'galleta' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Galleta`
                : itemType === 'galleta_especial' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Galleta Especial`
                : itemType === 'pack' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Pack`
                : itemType === 'pastel' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Pastel`
                : itemType === 'panaderia' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Panadería`
                : itemType === 'cachito' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Cachito`
                : itemType === 'desayuno' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Desayuno`
                : itemType === 'menu_sal' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Menú Salado`
                : itemType === 'bebida' ? `${currentItem?.id && typeof currentItem.id === 'string' ? 'Editar' : 'Agregar'} Bebida`
                : 'Editar Producto Personalizado'
              }
            </DialogTitle>
          </DialogHeader>
          
          {currentItem && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={currentItem.name || ''}
                  onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
                  placeholder="Nombre del producto"
                />
              </div>

              {itemType !== 'cachito' && (
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    value={currentItem.description || ''}
                    onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                    placeholder="Descripción del producto"
                    rows={3}
                  />
                </div>
              )}

              {itemType === 'brazo' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Precio Porción ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.priceSlice}
                        onChange={(e) => setCurrentItem({ ...currentItem, priceSlice: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Precio Completo ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.priceFull}
                        onChange={(e) => setCurrentItem({ ...currentItem, priceFull: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select 
                      value={currentItem.category} 
                      onValueChange={(value) => setCurrentItem({ ...currentItem, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Clásico">Clásico</SelectItem>
                        <SelectItem value="Especial">Especial</SelectItem>
                        <SelectItem value="Premium">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-amber-900 mb-2">📏 Información del Brazo:</p>
                    <p className="text-xs text-amber-800">{BRAZO_INFO.description}</p>
                  </div>

                  <div className="space-y-3">
                    <Label className="font-semibold">Disponibilidad por Tamaño:</Label>
                    <div className="flex items-center justify-between bg-cream p-3 rounded-lg">
                      <Label htmlFor="availableSlice">Porción Individual</Label>
                      <Switch
                        id="availableSlice"
                        checked={currentItem.availableSlice !== false}
                        onCheckedChange={(checked) => setCurrentItem({ ...currentItem, availableSlice: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between bg-cream p-3 rounded-lg">
                      <Label htmlFor="availableFull">Brazo Completo</Label>
                      <Switch
                        id="availableFull"
                        checked={currentItem.availableFull !== false}
                        onCheckedChange={(checked) => setCurrentItem({ ...currentItem, availableFull: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between bg-orange-50 p-3 rounded-lg border-2 border-orange-200">
                      <div>
                        <Label htmlFor="bajoPedido" className="font-semibold">🏷️ Bajo Pedido</Label>
                        <p className="text-xs text-gray-600 mt-1">Muestra badge en la foto del producto</p>
                      </div>
                      <Switch
                        id="bajoPedido"
                        checked={currentItem.bajoPedido === true}
                        onCheckedChange={(checked) => setCurrentItem({ ...currentItem, bajoPedido: checked })}
                      />
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="sortOrder" className="font-semibold">📊 Orden de Visualización</Label>
                          <p className="text-xs text-gray-600 mt-1">Número menor = aparece primero en el menú</p>
                        </div>
                        <Input
                          id="sortOrder"
                          type="number"
                          min="1"
                          max="999"
                          className="w-20 text-center"
                          value={currentItem.sortOrder || 999}
                          onChange={(e) => setCurrentItem({ ...currentItem, sortOrder: parseInt(e.target.value) || 999 })}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {itemType === 'galleta' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Precio Unidad ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.priceUnit || 1.50}
                      onChange={(e) => setCurrentItem({ ...currentItem, priceUnit: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pack x5 ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.pricePack5 || 6.00}
                      onChange={(e) => setCurrentItem({ ...currentItem, pricePack5: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pack x12 ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.pricePack12 || 14.00}
                      onChange={(e) => setCurrentItem({ ...currentItem, pricePack12: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>
              )}

              {itemType === 'pack' && (
                <>
                  <div className="space-y-2">
                    <Label>Slug (identificador único)</Label>
                    <Input
                      value={currentItem.slug || ''}
                      onChange={(e) => setCurrentItem({ ...currentItem, slug: e.target.value })}
                      placeholder="pack5, pack12, custom"
                    />
                    <p className="text-xs text-gray-600">Usa &apos;custom&apos; para el pack personalizado</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Tipo de Pack</Label>
                    <Select
                      value={currentItem.packType || 'fixed'}
                      onValueChange={(value) => setCurrentItem({ ...currentItem, packType: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecciona el tipo de pack" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">📦 Pack Fijo (cantidad y precio fijos)</SelectItem>
                        <SelectItem value="custom">🎨 Pack Personalizado (cliente elige cantidad)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {currentItem.packType === 'fixed' ? (
                    <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900">Configuración de Pack Fijo</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cantidad de Galletas</Label>
                          <Input
                            type="number"
                            value={currentItem.quantity || 5}
                            onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 5 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Precio ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentItem.price || 0}
                            onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-900">Configuración de Pack Personalizado</h4>
                      <p className="text-sm text-purple-800 mb-3">
                        📝 <strong>Lógica de Precios:</strong><br/>
                        • ≤12 galletas: Precio Base<br/>
                        • 13-48 galletas: $1.17 por unidad (aplica a todas)<br/>
                        • 49+ galletas: $1.10 por unidad (aplica a todas)
                      </p>
                      <div className="space-y-2">
                        <Label>Precio Base ($) - Para ≤12 galletas</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={currentItem.basePrice || 14.00}
                          onChange={(e) => setCurrentItem({ ...currentItem, basePrice: parseFloat(e.target.value) || 14.00 })}
                        />
                        <p className="text-xs text-gray-600">Por defecto: $14.00</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cantidad Mínima</Label>
                          <Input
                            type="number"
                            value={currentItem.minQuantity || 13}
                            onChange={(e) => setCurrentItem({ ...currentItem, minQuantity: parseInt(e.target.value) || 13 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cantidad Máxima</Label>
                          <Input
                            type="number"
                            value={currentItem.maxQuantity || 100}
                            onChange={(e) => setCurrentItem({ ...currentItem, maxQuantity: parseInt(e.target.value) || 100 })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Galleta Especial - Simple Product */}
              {itemType === 'galleta_especial' && (
                <div className="space-y-2">
                  <Label>Precio ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={currentItem.price || 0}
                    onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) })}
                  />
                </div>
              )}

              {itemType === 'pastel' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Precio Base ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.price || 0}
                        onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-gray-600">Precio base o precio de referencia</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Porciones</Label>
                      <Input
                        value={currentItem.portions || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, portions: e.target.value })}
                        placeholder="Ej: 12-15 personas"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 bg-purple-50 p-3 rounded-lg">
                    <Switch
                      id="isConfigurable"
                      checked={currentItem.isConfigurable || false}
                      onCheckedChange={(checked) => {
                        const updated = { ...currentItem, isConfigurable: checked };
                        if (checked && !updated.sizes) {
                          updated.sizes = []; // Initialize empty, user adds manually
                        }
                        setCurrentItem(updated);
                      }}
                    />
                    <Label htmlFor="isConfigurable" className="text-purple-800">
                      Es configurable (tiene tamaños personalizables)
                    </Label>
                  </div>
                  
                  {/* Dynamic Sizes Section - for ALL configurable cakes */}
                  {currentItem.isConfigurable && (
                    <div className="space-y-3 border-l-4 border-purple-300 pl-4 bg-purple-50 p-4 rounded-r-lg">
                      <div className="flex justify-between items-center">
                        <Label className="text-purple-900 font-semibold">Tamaños Disponibles</Label>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newSizes = [...(currentItem.sizes || []), { id: '', name: '', portions: '', price: 0 }];
                            setCurrentItem({ ...currentItem, sizes: newSizes });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 h-7"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Tamaño
                        </Button>
                      </div>
                      {(currentItem.sizes || []).length === 0 && (
                        <p className="text-sm text-purple-700 italic">No hay tamaños. Click &quot;Agregar Tamaño&quot; para empezar.</p>
                      )}
                      {(currentItem.sizes || []).map((size, index) => (
                        <div key={index} className="grid grid-cols-4 gap-2 bg-white p-3 rounded border border-purple-200">
                          <div className="space-y-1">
                            <Label className="text-xs">ID</Label>
                            <Input
                              value={size.id || ''}
                              onChange={(e) => {
                                const newSizes = [...(currentItem.sizes || [])];
                                newSizes[index] = { ...size, id: e.target.value };
                                setCurrentItem({ ...currentItem, sizes: newSizes });
                              }}
                              placeholder="mediana"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre</Label>
                            <Input
                              value={size.name || ''}
                              onChange={(e) => {
                                const newSizes = [...(currentItem.sizes || [])];
                                newSizes[index] = { ...size, name: e.target.value };
                                setCurrentItem({ ...currentItem, sizes: newSizes });
                              }}
                              placeholder="Mediana"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Porciones</Label>
                            <Input
                              value={size.portions || ''}
                              onChange={(e) => {
                                const newSizes = [...(currentItem.sizes || [])];
                                newSizes[index] = { ...size, portions: e.target.value };
                                setCurrentItem({ ...currentItem, sizes: newSizes });
                              }}
                              placeholder="12-15 personas"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Precio ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={size.price || 0}
                              onChange={(e) => {
                                const newSizes = [...(currentItem.sizes || [])];
                                newSizes[index] = { ...size, price: parseFloat(e.target.value) || 0 };
                                setCurrentItem({ ...currentItem, sizes: newSizes });
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div className="col-span-4 flex justify-end">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newSizes = (currentItem.sizes || []).filter((_, i) => i !== index);
                                setCurrentItem({ ...currentItem, sizes: newSizes });
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {itemType === 'panaderia' && (
                <>
                  <div className="space-y-2">
                    <Label>Precio Base ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.price || 0}
                      onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-gray-600">Si tiene opciones, este será el precio base</p>
                  </div>
                  <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-lg">
                    <Switch
                      id="hasOption"
                      checked={currentItem.hasOption || false}
                      onCheckedChange={(checked) => {
                        const updated = { ...currentItem, hasOption: checked };
                        if (checked && !updated.options) {
                          // Initialize with one default option
                          updated.options = [{ name: '', price: 0 }];
                        }
                        setCurrentItem(updated);
                      }}
                    />
                    <Label htmlFor="hasOption" className="text-blue-800">Tiene variantes de precio (ej: Tradicional, Con Queso Crema)</Label>
                  </div>
                  
                  {currentItem.hasOption && (
                    <div className="space-y-3 border-l-4 border-blue-300 pl-4 bg-blue-50 p-4 rounded-r-lg">
                      <div className="flex justify-between items-center">
                        <Label className="text-blue-900 font-semibold">Variantes del Producto</Label>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newOptions = [...(currentItem.options || []), { name: '', price: 0 }];
                            setCurrentItem({ ...currentItem, options: newOptions });
                          }}
                          className="bg-blue-600 hover:bg-blue-700 h-7"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Variante
                        </Button>
                      </div>
                      {(currentItem.options || []).length === 0 && (
                        <p className="text-sm text-blue-700 italic">No hay variantes. Click "Agregar Variante" para empezar.</p>
                      )}
                      {(currentItem.options || []).map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2 bg-white p-3 rounded border border-blue-200">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre de la Opción</Label>
                            <Input
                              value={option.name || ''}
                              onChange={(e) => {
                                const newOptions = [...(currentItem.options || [])];
                                newOptions[index] = { ...option, name: e.target.value };
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              placeholder="Ej: Tradicional"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Precio ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={option.price || 0}
                              onChange={(e) => {
                                const newOptions = [...(currentItem.options || [])];
                                newOptions[index] = { ...option, price: parseFloat(e.target.value) || 0 };
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div className="col-span-2 flex justify-end">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newOptions = (currentItem.options || []).filter((_, i) => i !== index);
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {itemType === 'cachito' && (
                <div className="space-y-2">
                  <Label>Precio ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={currentItem.price || 0}
                    onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) })}
                  />
                </div>
              )}

              {/* Desayunos & Menu Salado - Similar to Panaderia */}
              {(itemType === 'desayuno' || itemType === 'menu_sal') && (
                <>
                  <div className="space-y-2">
                    <Label>Precio Base ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.price || 0}
                      onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) })}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">¿Tiene opciones/variantes?</Label>
                      <Switch
                        checked={currentItem.hasOption || false}
                        onCheckedChange={(checked) => setCurrentItem({ ...currentItem, hasOption: checked, options: checked ? currentItem.options || [] : [] })}
                      />
                    </div>

                    {currentItem.hasOption && (
                      <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-900 font-semibold mb-2">Opciones/Variantes:</p>
                        {(currentItem.options || []).map((option, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <Input
                              placeholder="Nombre (ej: Completo)"
                              value={option.name || ''}
                              onChange={(e) => {
                                const newOptions = [...currentItem.options];
                                newOptions[idx].name = e.target.value;
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Precio"
                              value={option.price || 0}
                              onChange={(e) => {
                                const newOptions = [...currentItem.options];
                                newOptions[idx].price = parseFloat(e.target.value) || 0;
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="w-24"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newOptions = currentItem.options.filter((_, i) => i !== idx);
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentItem({
                              ...currentItem,
                              options: [...(currentItem.options || []), { name: '', price: 0 }]
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Opción
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Bebidas - With options/variants support (like Menu Salado) */}
              {itemType === 'bebida' && (
                <>
                  <div className="space-y-2">
                    <Label>Tipo de Bebida *</Label>
                    <Select
                      value={currentItem.subcategory || 'frias'}
                      onValueChange={(value) => setCurrentItem({ ...currentItem, subcategory: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="frias">🧊 Bebida Fría</SelectItem>
                        <SelectItem value="calientes">☕ Bebida Caliente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Precio Base ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.price || 0}
                      onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) })}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">¿Tiene opciones/variantes?</Label>
                      <Switch
                        checked={currentItem.hasOption || false}
                        onCheckedChange={(checked) => setCurrentItem({ ...currentItem, hasOption: checked, options: checked ? currentItem.options || [] : [] })}
                      />
                    </div>

                    {currentItem.hasOption && (
                      <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-900 font-semibold mb-2">Opciones/Variantes:</p>
                        {(currentItem.options || []).map((option, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <Input
                              placeholder="Nombre (ej: Americano)"
                              value={option.name || ''}
                              onChange={(e) => {
                                const newOptions = [...currentItem.options];
                                newOptions[idx].name = e.target.value;
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Precio"
                              value={option.price || 0}
                              onChange={(e) => {
                                const newOptions = [...currentItem.options];
                                newOptions[idx].price = parseFloat(e.target.value) || 0;
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                              className="w-24"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newOptions = currentItem.options.filter((_, i) => i !== idx);
                                setCurrentItem({ ...currentItem, options: newOptions });
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentItem({
                              ...currentItem,
                              options: [...(currentItem.options || []), { name: '', price: 0 }]
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Opción
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Custom Category Product - Simple Price */}
              {currentItem?.categoryId && (
                <div className="space-y-2">
                  <Label>Precio ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={currentItem.price || 0}
                    onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              )}

              {itemType !== 'cachito' && (
                <div className="space-y-2">
                  <Label>URL de Imagen</Label>
                  <Input
                    value={currentItem.image || ''}
                    onChange={(e) => setCurrentItem({ ...currentItem, image: e.target.value })}
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                  {currentItem.image && (
                    <img src={currentItem.image} alt="Preview" className="w-full h-48 object-cover rounded-lg mt-2" />
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  checked={currentItem.available !== false}
                  onCheckedChange={(checked) => setCurrentItem({ ...currentItem, available: checked })}
                />
                <Label>Disponible</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200">
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {currentCategory?.id?.startsWith('cat_') ? 'Nueva Categoría' : 'Editar Categoría'}
            </DialogTitle>
          </DialogHeader>
          
          {currentCategory && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre de la Categoría *</Label>
                <Input
                  value={currentCategory.name}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                  placeholder="Ej: Cakes, Pasteles, Tortas..."
                />
              </div>

              <div className="space-y-2">
                <Label>Icono (emoji)</Label>
                <Input
                  value={currentCategory.icon}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, icon: e.target.value })}
                  placeholder="🎂"
                  className="text-2xl"
                />
                <p className="text-xs text-chocolate-light">
                  Sugerencias: 🎂 🍰 🧁 🥮 🍩 🥐 🥖 🍞 🥯
                </p>
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={currentCategory.description}
                  onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}
                  placeholder="Descripción breve de la categoría"
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleSaveCategory} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
